import { Box, Grid, Paper, CircularProgress } from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import DigitalSidebar from "../../components/digitalSidebar";
import Header from "../../components/header";
import PDFViewer from "../../components/PDFViewr";
import SideBar from "../../components/sideBar";
import * as bookServices from "../../services/book.services";
import "./digitalbook.css";

const DigitalBook = () => {
  const bookId = useParams().bookId;
  const [book, setBook] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // State to manage sidebar visibility
  const [isOpen, setIsOpen] = useState(true); // Default to open
  const sidebarRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Add states for caching
  const [isPDFCached, setIsPDFCached] = useState(false);
  const [isCaching, setIsCaching] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  
  // Create a custom page setter that both updates the page and scrolls the sidebar
  const handleSetPageNumber = (newPageNumber) => {
    setPageNumber(newPageNumber);
    // Page is already being passed as currentPage prop to DigitalSidebar
    // The useEffect in DigitalSidebar will handle scrolling
  };
  
  useEffect(() => {
    setIsLoading(true);
    bookServices
      .getBookById(bookId)
      .then((response) => {
        setBook(response.data);
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  }, [bookId]);
  // Helper for consistent CloudFront URL
  const getCloudFrontUrl = (url) => {
    if (!url) return "";
    return url.replace(
      "https://redsea-books-assets.s3.eu-central-1.amazonaws.com",
      "https://dng2cduv9v2yj.cloudfront.net"
    );
  };

  // Check if the PDF is cached and auto-cache if not present
  useEffect(() => {
    const checkAndCachePDF = async () => {
      if (!book.digitalEdition) return;
      const pdfUrl = getCloudFrontUrl(book.digitalEdition);
      
      try {
        const cache = await caches.open("pdf-cache-v1"); // P0: was "appV1"
        
        // Check if digital edition is cached
        const pdfResponse = await cache.match(pdfUrl);
        if (pdfResponse) {
          setIsPDFCached(true);
          return; // Already cached, exit function
        }
        
        // Background caching - don't block PDF rendering
        setTimeout(async () => {
          // Auto-cache the PDF file since it's not cached
          console.log("Auto-caching PDF file in background...");
          setIsCaching(true);
          setDownloadProgress(0);
          
          try {
            const response = await fetch(pdfUrl);
            if (!response.ok) {
              throw new Error(`Failed to fetch PDF: ${response.status}`);
            }
            
            // Get content length for progress calculation
            let contentLength = +response.headers.get("Content-Length");
            const reader = response.body.getReader();
            let receivedLength = 0;
            const chunks = [];
            
            // Process the stream
            while (true) {
              const { done, value } = await reader.read();
              if (done) {
                // If contentLength was missing, set it to 100% when done
                if (!contentLength) setDownloadProgress(100);
                break;
              }
              
              chunks.push(value);
              receivedLength += value.length;
              
              // Calculate and update progress if we have a valid length
              if (contentLength) {
                const progress = Math.round((receivedLength / contentLength) * 100);
                setDownloadProgress(progress);
              } else {
                // Indeterminate progress: just cap it at 99% until done
                const simulatedProgress = Math.min(99, Math.round((receivedLength / (receivedLength + 1000000)) * 100));
                setDownloadProgress(simulatedProgress);
              }
            }
            
            // Create a new response from the chunks
            const blob = new Blob(chunks);
            const newResponse = new Response(blob, {
              headers: {
                "Content-Type": response.headers.get("Content-Type")
              }
            });
            
            // Store in cache
            // Cache under the clean URL (no Range headers) so SW can always match it
            await cache.put(pdfUrl, newResponse);
            
            setIsPDFCached(true);
            console.log("PDF auto-cached successfully");
          } catch (error) {
            console.error("Error auto-caching PDF:", error);
          } finally {
            setIsCaching(false);
          }
        }, 2000); // Start caching after 2 seconds to allow the PDF to start loading first
      } catch (error) {
        console.error("Error checking PDF cache:", error);
      }
    };
    
    if (book.digitalEdition) {
      checkAndCachePDF();
    }
  }, [book]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
    setIsOpen(!isSidebarOpen);
  };

  // Add a handler for fullscreen changes
  const handleFullscreenChange = (fullscreenState) => {
    setIsFullscreen(fullscreenState);
  };

  // Handle download progress updates
  const handleDownloadProgress = (progress) => {
    setDownloadProgress(progress);
  };

  return (
    <>
      <Header />

      <Box sx={{ p: 2 }}>
        <Grid container spacing={2} sx={{ position: "relative" }}>
          <SideBar />

          {/* Only show the sidebar when not in fullscreen */}
          {!isFullscreen && (
            <Grid
              className={`sidebar-grid ${!isSidebarOpen ? "minimized" : ""}`}
              item
              xs={12}
              sm="auto"
              sx={{
                overflow: "hidden",
                position: "relative",
              }}
            >
              <DigitalSidebar
                book={book}
                setPageNumber={handleSetPageNumber}
                currentPage={pageNumber}
                isOpen={isOpen}
                onToggle={toggleSidebar}
                ref={sidebarRef}
              />
            </Grid>
          )}

          <Grid
            item
            xs={12}
            sm
            className="main-content-grid"
            sx={{
              flexGrow: 1,
              overflow: "hidden",
              marginLeft: 0,
            }}
          >
            {isLoading ? (
              <Paper sx={{ p: 2 }}>
                <CircularProgress />
                <span style={{ marginLeft: "10px" }}>Loading...</span>
              </Paper>
            ) : (
              <PDFViewer
                propPageNumber={pageNumber}
                mode="digital"
                bookId={bookId}
                sample={false}
                setPageNumber={handleSetPageNumber}
                onFullscreenChange={handleFullscreenChange}
                isPDFCached={isPDFCached}
                onDownloadProgress={handleDownloadProgress}
                fullscreenSidebar={false}
                isCachingInBackground={isCaching}
                cachingProgress={downloadProgress}
                pdfURL={getCloudFrontUrl(book.digitalEdition)}
              />
            )}
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default DigitalBook;
